import { Content, Item, Root, Trigger } from '@radix-ui/react-accordion';
import * as stylex from '@stylexjs/stylex';
import { clsx } from 'clsx';
import { controlMetrics } from '../theme/controls.stylex';
import type { AccordionProps } from './Accordion.types';

const slideUp = stylex.keyframes({ from: { height: 'var(--radix-accordion-content-height)' }, to: { height: 0 } });
const slideDown = stylex.keyframes({ from: { height: 0 }, to: { height: 'var(--radix-accordion-content-height)' } });
const styles = stylex.create({
  root: {
    width: '100%',
    borderRadius: controlMetrics.radius,
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
  },
  trigger: {
    cursor: 'default',
    backgroundColor: 'transparent',
    borderWidth: 0,
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
  },
  grow: { flex: '1 1 0%' },
  fullWidth: { width: '100%' },
  item: { display: 'flex', width: '100%', flexDirection: 'column', padding: 0 },
  header: { display: 'flex', width: '100%', alignItems: 'center' },
  content: {
    overflow: 'hidden',
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
    animationDuration: '0.3s',
    animationTimingFunction: 'ease-in-out',
    animationName: { 'default': 'none', ':is([data-state="closed"])': slideUp, ':is([data-state="open"])': slideDown },
  },
});

const Accordion = ({
  accordionItems,
  defaultValue,
  value,
  onItemSelect,
  disabled,
  extraClassNames,
  xstyle,
  testId,
}: AccordionProps) => {
  const rootProps = stylex.props(styles.root, xstyle);
  return (
    <Root
      {...rootProps}
      className={clsx(rootProps.className, extraClassNames)}
      type="single"
      defaultValue={defaultValue}
      value={value}
      collapsible
      data-testid={testId}
      onValueChange={(_value) => onItemSelect(_value)}>
      {accordionItems.map((item) => {
        const trigger = (
          <Trigger
            disabled={disabled}
            {...stylex.props(styles.trigger, item.headerActions ? styles.grow : styles.fullWidth)}>
            {item.trigger}
          </Trigger>
        );
        const contentProps = stylex.props(styles.content, item.contentXstyle);
        return (
          <Item {...stylex.props(styles.item)} key={item.value} disabled={disabled} value={item.value}>
            {item.headerActions ? (
              <div {...stylex.props(styles.header)}>
                {trigger}
                {item.headerActions}
              </div>
            ) : (
              trigger
            )}
            <Content {...contentProps} className={clsx(contentProps.className, item.contentClassNames)}>
              {item.content}
            </Content>
          </Item>
        );
      })}
    </Root>
  );
};

export default Accordion;
